import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { FollowService } from '@/services/followService';
import { toast } from 'sonner';

export interface SuggestedUser {
  user_id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  followers_count: number;
  posts_count: number;
  pets_count: number;
}

export const useSuggestedUsers = () => {
  const [suggestedUsers, setSuggestedUsers] = useState<SuggestedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchSuggestedUsers = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      // Get users that the current user is not already following
      const { data: followingData } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', user.id);

      const followingIds = followingData?.map(f => f.following_id) || [];

      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, username, display_name, avatar_url, followers_count')
        .neq('user_id', user.id)
        .not('user_id', 'in', `(${followingIds.join(',')})`)
        .limit(8);

      if (error) throw error;

      // Get real counts for each user
      const usersWithRealCounts = await Promise.all(
        (data || []).map(async (profile) => {
          const [postsResult, petsResult] = await Promise.all([
            supabase
              .from('posts')
              .select('id', { count: 'exact', head: true })
              .eq('user_id', profile.user_id)
              .eq('is_public', true),
            supabase
              .from('pets')
              .select('id', { count: 'exact', head: true })
              .eq('user_id', profile.user_id)
              .eq('is_public', true)
          ]);

          return {
            ...profile,
            posts_count: postsResult.count || 0,
            pets_count: petsResult.count || 0
          };
        })
      );

      setSuggestedUsers(usersWithRealCounts);
    } catch (error) {
      console.error('Error fetching suggested users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFollowUser = async (targetUserId: string) => {
    if (!user) return;

    try {
      const result = await FollowService.followUser(targetUserId);
      
      if (result.success) {
        toast.success('Utente seguito con successo');
        // Remove the followed user from suggestions
        setSuggestedUsers(prev => prev.filter(u => u.user_id !== targetUserId));
      } else {
        toast.error(result.error || 'Errore nel seguire l\'utente');
      }
    } catch (error) {
      console.error('Error following user:', error);
      toast.error('Errore nel seguire l\'utente');
    }
  };

  useEffect(() => {
    fetchSuggestedUsers();
  }, [user]);

  return {
    suggestedUsers,
    loading,
    handleFollowUser,
    refetch: fetchSuggestedUsers
  };
};